/**
 * Clinical → eTMF completeness (`/solutions/clinical/etmf-completeness`) —
 * the Study check (ADR-0036 + ADR-0042, the DAQUMA AI completeness algorithm):
 * each trial links a Veeva study record, its Protocol and SOPs, and the
 * expected DIA TMF artifacts; a check scores one representative document per
 * artifact 0–100 for Existence, vs TMF, vs SOP, vs Protocol and Quality
 * (ALCOA+), lists missing artifacts, and compares the Protocol with the Veeva
 * study metadata. Rows can be assigned to reviewers (ADR-0043). Every role;
 * the field mapping is Developer+.
 *
 * Never "coming soon": each missing input is an empty state that says what to
 * do — add a Veeva connection, create a trial, upload a Protocol.
 */

import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Container,
  Group,
  Loader,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { IconArrowLeft, IconPlus, IconSettings } from "@tabler/icons-react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { useAccessToken } from "@/features/auth/useAccessToken";
import { useEffectiveRoles } from "@/features/auth/RolePreviewContext";
import { AdvisoryBanner } from "@/features/solutions/shared/AdvisoryBanner";
import { useMe } from "@/features/users/useMe";
import {
  getEtmfMapping,
  getReferenceModel,
  getTrial,
  listEtmfConnections,
  listTrials,
  type EtmfConnectionOut,
  type EtmfMappingOut,
  type ReferenceModelOut,
  type TrialDetailOut,
  type TrialOut,
} from "@/shared/api/etmfCompleteness";
import { toastApiError } from "@/shared/ui/toast";
import { MappingEditor } from "./MappingEditor";
import { NewTrialModal } from "./NewTrialModal";
import { TrialDetail } from "./TrialDetail";
import {
  DEFAULT_AMBER_MIN,
  DEFAULT_GREEN_MIN,
  RAG_COLOR,
  RAG_LABEL,
  formatUtcShort,
  ragOf,
} from "./completenessMeta";

function StudyCheck() {
  const getToken = useAccessToken();
  const { data: me } = useMe();
  const roles = useEffectiveRoles(me?.roles ?? []);
  const developer = roles.includes("developer") || roles.includes("administrator");
  const [params, setParams] = useSearchParams();
  const trialId = params.get("trial");

  const [connections, setConnections] = useState<EtmfConnectionOut[] | null>(null);
  // The ones this person may operate; the rest are shown, disabled, with their owner.
  const usable = (connections ?? []).filter((c) => c.usable);
  const [model, setModel] = useState<ReferenceModelOut | null>(null);
  const [trials, setTrials] = useState<TrialOut[] | null>(null);
  const [detail, setDetail] = useState<TrialDetailOut | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [mappingFor, setMappingFor] = useState<string | null>(null);
  const [mapping, setMapping] = useState<EtmfMappingOut | null>(null);

  const loadList = useCallback(async () => {
    const token = await getToken();
    const [c, m, t] = await Promise.all([
      listEtmfConnections(token),
      getReferenceModel(token),
      listTrials(token),
    ]);
    setConnections(c.items);
    setModel(m);
    setTrials(t.items);
  }, [getToken]);

  useEffect(() => {
    loadList().catch((err: unknown) =>
      setError(err instanceof Error ? err.message : "Could not load the page."),
    );
  }, [loadList]);

  const loadDetail = useCallback(async () => {
    if (!trialId) {
      setDetail(null);
      return;
    }
    try {
      setDetail(await getTrial(await getToken(), trialId));
    } catch (err) {
      toastApiError("Could not load the trial", err);
    }
  }, [getToken, trialId]);

  useEffect(() => {
    void loadDetail();
  }, [loadDetail]);

  // A check started by someone else: re-read the trial until it ends.
  const othersRunning =
    detail?.last_run !== null &&
    detail?.last_run !== undefined &&
    !detail.last_run.mine &&
    (detail.last_run.status === "queued" || detail.last_run.status === "running");
  useEffect(() => {
    if (!othersRunning) return;
    const t = setInterval(() => void loadDetail(), 3000);
    return () => clearInterval(t);
  }, [othersRunning, loadDetail]);

  const openMapping = async (connectionId: string) => {
    try {
      setMapping(await getEtmfMapping(await getToken(), connectionId));
      setMappingFor(connectionId);
    } catch (err) {
      toastApiError("Could not load the field mapping", err);
    }
  };

  const green = model?.rag_green_min ?? DEFAULT_GREEN_MIN;
  const amber = model?.rag_amber_min ?? DEFAULT_AMBER_MIN;

  let body: React.ReactNode;
  if (error) {
    body = (
      <Alert color="red" title="Could not load eTMF completeness">
        {error}
      </Alert>
    );
  } else if (connections === null || trials === null || model === null) {
    body = <Loader aria-label="Loading" />;
  } else if (usable.length === 0) {
    // Seeing a connection and using it are different grants (ADR-0015 §5): an
    // Administrator sees every connection under Integrations but may operate
    // only the ones they own or were given `use` on. So when connections exist
    // that are not shared with this person, say exactly that and whom to ask,
    // instead of the misleading "no connection".
    const unshared = connections.filter((c) => !c.usable);
    body = (
      <Paper withBorder radius="md" p="xl">
        <Stack gap="sm" align="flex-start">
          <Title order={4}>
            {unshared.length ? "No Veeva connection shared with you" : "No Veeva connection"}
          </Title>
          {unshared.length > 0 && (
            <Text size="sm">
              {unshared.length === 1
                ? "This connection exists but is"
                : "These connections exist but are"}{" "}
              not shared with you:{" "}
              {unshared.map((c, i) => (
                <span key={c.connection_id}>
                  {i > 0 && ", "}
                  <strong>{c.display_name}</strong>
                  {c.owner_display_name ? ` (owner: ${c.owner_display_name})` : ""}
                </span>
              ))}
              . Ask the owner to share it with <em>use</em> — with you, or with the Administrator
              role.
            </Text>
          )}
          <Text size="sm">
            Completeness reads a trial's study record and documents from a Veeva Vault you may use.
            Add one under{" "}
            <Anchor component={RouterLink} to="/integration/connections/new">
              Integrations → Add connection
            </Anchor>
            {unshared.length ? "." : ", or ask its owner to share it with you."}
          </Text>
        </Stack>
      </Paper>
    );
  } else if (trialId) {
    body = detail ? (
      <Stack gap="md">
        <Group justify="space-between">
          <Button
            variant="subtle"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => {
              setParams({ tab: "study" });
              void loadList();
            }}
          >
            All trials
          </Button>
          <Button
            variant="subtle"
            leftSection={<IconSettings size={16} />}
            onClick={() => void openMapping(detail.connection_id)}
          >
            Field mapping
          </Button>
        </Group>
        <TrialDetail
          trial={detail}
          greenMin={green}
          amberMin={amber}
          onChange={setDetail}
          reload={loadDetail}
        />
      </Stack>
    ) : (
      <Loader aria-label="Loading trial" />
    );
  } else {
    body = (
      <Stack gap="md">
        <Group justify="space-between">
          <Text size="sm" c="dimmed">
            {trials.length} trial{trials.length === 1 ? "" : "s"} · DIA TMF Reference Model seed{" "}
            {model.version}
          </Text>
          <Group gap="xs">
            {usable.length === 1 && (
              <Button
                variant="subtle"
                leftSection={<IconSettings size={16} />}
                onClick={() => void openMapping(usable[0]!.connection_id)}
              >
                Field mapping
              </Button>
            )}
            {usable.length > 1 && (
              <Select
                placeholder="Field mapping…"
                data={usable.map((c) => ({ value: c.connection_id, label: c.display_name }))}
                value={null}
                onChange={(v) => v && void openMapping(v)}
                w={220}
              />
            )}
            <Button leftSection={<IconPlus size={16} />} onClick={() => setCreating(true)}>
              New trial
            </Button>
          </Group>
        </Group>
        {trials.length === 0 ? (
          <Paper withBorder radius="md" p="xl">
            <Stack gap="sm" align="flex-start">
              <Title order={4}>No trial yet</Title>
              <Text size="sm">
                Create a trial: pick a Veeva study record, upload its Protocol and SOPs, keep the
                essential TMF artifacts or choose your own, then run the completeness check.
              </Text>
              <Button leftSection={<IconPlus size={16} />} onClick={() => setCreating(true)}>
                New trial
              </Button>
            </Stack>
          </Paper>
        ) : (
          <Table.ScrollContainer minWidth={720}>
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Trial</Table.Th>
                  <Table.Th>Study</Table.Th>
                  <Table.Th>Overall</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Missing</Table.Th>
                  <Table.Th>Last checked</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {trials.map((t) => {
                  const rag = ragOf(t.summary?.overall, green, amber);
                  return (
                    <Table.Tr
                      key={t.trial_id}
                      style={{ cursor: "pointer" }}
                      onClick={() => setParams({ tab: "study", trial: t.trial_id })}
                      data-testid="trial-row"
                    >
                      <Table.Td>
                        <Text size="sm" fw={500}>
                          {t.name}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {t.connection_name}
                          {t.has_protocol ? "" : " · no protocol"}
                        </Text>
                      </Table.Td>
                      <Table.Td>{t.study_label}</Table.Td>
                      <Table.Td>
                        {t.summary?.overall != null ? `${t.summary.overall}%` : "—"}
                      </Table.Td>
                      <Table.Td>
                        <Badge color={RAG_COLOR[rag]} variant="light">
                          {RAG_LABEL[rag]}
                        </Badge>
                      </Table.Td>
                      <Table.Td>{t.summary ? t.summary.missing : "—"}</Table.Td>
                      <Table.Td>{formatUtcShort(t.last_checked_at_utc)}</Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Stack>
    );
  }

  return (
    <>
      <Text c="dimmed" size="sm">
        Is every expected TMF document of a Veeva study there, and complete against the TMF
        Reference Model, your SOPs and the Protocol?
      </Text>
      {body}
      {connections && model && creating && (
        <NewTrialModal
          opened={creating}
          onClose={() => setCreating(false)}
          connections={connections}
          artifacts={model.artifacts}
          onCreated={(t) => {
            setCreating(false);
            setParams({ tab: "study", trial: t.trial_id });
            void loadList();
          }}
        />
      )}
      {mapping && mappingFor && (
        <MappingEditor
          opened
          onClose={() => setMappingFor(null)}
          mapping={mapping}
          canEdit={developer}
          onSaved={(m) => setMapping(m)}
        />
      )}
    </>
  );
}

export function EtmfCompletenessPage() {
  return (
    <Container size="xl" py="md">
      <Stack gap="md">
        <div>
          <Title order={2}>eTMF completeness</Title>
          <Text c="dimmed" size="sm">
            Is the trial's TMF complete? Every expected artifact checked in Veeva against the TMF
            definition, your SOPs and the Protocol, with ALCOA+ quality and the study metadata.
          </Text>
        </div>
        <AdvisoryBanner />
        <StudyCheck />
      </Stack>
    </Container>
  );
}
